import { Box, Center, Icon, Text, VStack } from '@chakra-ui/react';
import { Dropzone } from '@diamond/shared/ui';
import { CloudUpload } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Accept } from 'react-dropzone';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';

interface ImageDropzoneProps<T extends FieldValues = FieldValues> {
  name: string;
  accept?: Accept;
  maxSize?: number;
  errors?: FieldErrors<T>;
  placeholder?: string;
  onFileUpload?: (files: File[]) => void;
  isUploading?: boolean;
  onRemove?: () => void;
  disabled?: boolean;
}

export function ImageDropzone<T extends FieldValues = FieldValues>({
  name,
  accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
  },
  maxSize = 2,
  errors,
  placeholder: _placeholder = 'Drag and drop file here',
  onFileUpload,
  isUploading = false,
  onRemove,
  disabled = false,
}: ImageDropzoneProps<T>) {
  const { setValue, watch, getValues } = useFormContext();
  const previewUrl: string = watch(name) || '';
  const fileFieldName = useMemo(() => `${name}File`, [name]);
  const fileFieldValue = watch(fileFieldName) as File[] | null | undefined;
  const previewRef = useRef<string | null>(null);
  const removalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousFilesRef = useRef<File[] | null | undefined>(fileFieldValue);

  const errorForm = errors ? errors[name]?.message : '';

  useEffect(() => {
    return () => {
      if (previewRef.current && previewRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(previewRef.current);
      }
    };
  }, []);

  const cleanupPreviewUrl = useCallback(() => {
    if (previewRef.current && previewRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(previewRef.current);
    }
    previewRef.current = null;
  }, []);

  const handleRemove = useCallback(() => {
    cleanupPreviewUrl();
    if (onRemove) {
      onRemove();
    } else {
      const currentUrl = getValues(name);
      if (
        currentUrl &&
        typeof currentUrl === 'string' &&
        currentUrl.startsWith('blob:')
      ) {
        URL.revokeObjectURL(currentUrl);
      }
      setValue(name, '', {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    setValue(fileFieldName, null, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [cleanupPreviewUrl, getValues, name, onRemove, setValue, fileFieldName]);

  const handleFileSelect = useCallback(
    (files: File[]) => {
      if (!files?.length) return;

      if (onFileUpload) {
        onFileUpload(files);
        return;
      }

      const [file] = files;
      const objectUrl = URL.createObjectURL(file);

      cleanupPreviewUrl();
      previewRef.current = objectUrl;

      setValue(name, objectUrl, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(fileFieldName, files, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [cleanupPreviewUrl, fileFieldName, onFileUpload, setValue, name]
  );

  useEffect(() => {
    if (removalTimeoutRef.current) {
      clearTimeout(removalTimeoutRef.current);
      removalTimeoutRef.current = null;
    }

    const hadPreviousFile =
      Array.isArray(previousFilesRef.current) &&
      previousFilesRef.current.length > 0;
    const hasCurrentFile =
      Array.isArray(fileFieldValue) && fileFieldValue.length > 0;

    if (hadPreviousFile && !hasCurrentFile) {
      removalTimeoutRef.current = setTimeout(() => {
        const currentFiles = getValues(fileFieldName);
        if (Array.isArray(currentFiles) && currentFiles.length > 0) {
          return;
        }
        handleRemove();
      }, 0);
    }

    previousFilesRef.current = fileFieldValue;

    return () => {
      if (removalTimeoutRef.current) {
        clearTimeout(removalTimeoutRef.current);
        removalTimeoutRef.current = null;
      }
    };
  }, [fileFieldValue, fileFieldName, getValues, handleRemove]);

  // Show loading state
  if (isUploading) {
    return (
      <Box>
        <Box
          border="2px dashed"
          borderColor="blue.400"
          borderRadius="8px"
          p={6}
          bg="blue.50"
        >
          <Center>
            <VStack spacing={2}>
              <Icon as={CloudUpload} w={8} h={8} color="blue.500" />
              <Text fontSize="sm" color="blue.600" fontWeight="medium">
                Uploading image...
              </Text>
            </VStack>
          </Center>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Dropzone
        key={previewUrl || 'dropzone'}
        name={fileFieldName}
        accept={accept}
        maxSize={maxSize}
        showPreviews
        urlDownload={previewUrl || undefined}
        onFileSelect={handleFileSelect}
        disabled={disabled}
      />

      {errorForm && (
        <Text color="#FF383C" fontSize="sm" mt={2}>
          {errorForm as string}
        </Text>
      )}
    </Box>
  );
}
